import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight, Volume2, VolumeX, X, Smartphone, Store, Landmark } from "lucide-react";

const GOLD = "#eec643";
const VIOLET = "#7a5af8";

/* Storyboard per the Kinetic 3D Video Directive — SC 01–10, obsidian/gold/violet color script */
const SCENES = [
  { img: "/assets/film/sc01-coldopen.png", tag: "SC 01 · COLD OPEN", title: "The Vacuum Thesis",
    captions: ["CASH. IN. MOTION.", "NO GATEKEEPERS.", "BGNZN"], move: "dolly-in", duration: 9000 },
  { img: "/assets/film/sc02-frontdoor.png", tag: "SC 02 · THE FRONT DOOR", title: "Three Doors, One Vault",
    captions: ["MEMBER WALLET", "MERCHANT REGISTER", "SOVEREIGN PORTAL", "COUNCIL"], move: "orbit", duration: 11000 },
  { img: "/assets/film/sc03-wallet.png", tag: "SC 03 · MEMBER WALLET", title: "Your Wallet Is Already Yours",
    captions: ["UUID + PHONE = YOUR KEYS", "LIVE · CHAIN 9940"], move: "rise", duration: 9000 },
  { img: "/assets/film/sc04-cashin.png", tag: "SC 04 · CASH-IN", title: "Three Rails, Same Vault",
    captions: ["WIRE IT.", "HAND IT.", "WALK IN WITH IT.", "SETTLED. T+0."], move: "pan-left", duration: 12000, whip: true },
  { img: "/assets/film/sc05-p2p.png", tag: "SC 05 · P2P", title: "Moving It Like a Text",
    captions: ["SCAN. CONFIRM. SETTLED.", "ZERO FEES INSIDE THE LOOP"], move: "drift", duration: 9000, whip: true },
  { img: "/assets/film/sc06-register.png", tag: "SC 06 · THE REGISTER", title: "Merchant Side",
    captions: ["CHARGE. CHIME. NEXT CUSTOMER."], move: "dolly-in", duration: 8000, chime: true },
  { img: "/assets/film/sc07-desk.png", tag: "SC 07 · THE DESK", title: "The Market Maker's Cockpit",
    captions: ["FUNDING DESK", "SMART ESCROW", "FORWARD LIQUIDATION", "PROOF OF RESERVE"], move: "crane", duration: 12000 },
  { img: "/assets/film/sc08-cashout.png", tag: "SC 08 · CASH-OUT", title: "The Loop Closes",
    captions: ["BURN. COUNT. WALK AWAY."], move: "pan-right", duration: 9000, whip: true },
  { img: "/assets/film/sc09-vault.png", tag: "SC 09 · THE VAULT IS REAL", title: "Hardware Finale",
    captions: ["EVERY KEY. IN SILICON.", "NOTHING. ON. DISK."], move: "orbit", duration: 9000 },
  { img: "/assets/film/sc10-endcard.png", tag: "SC 10 · CALL TO ACTION", title: "bgnzn.org",
    captions: ["CASH IN.", "MOVE FREE.", "CASH OUT."], move: "dolly-out", duration: 14000, endcard: true },
];

/* camera choreography — slow, heavy, confident (directive: no drone-float) */
const MOVES = {
  "dolly-in":  { initial: { scale: 1.02 }, animate: { scale: 1.16 } },
  "dolly-out": { initial: { scale: 1.16 }, animate: { scale: 1.02 } },
  "pan-left":  { initial: { scale: 1.14, x: "2.5%" }, animate: { scale: 1.14, x: "-2.5%" } },
  "pan-right": { initial: { scale: 1.14, x: "-2.5%" }, animate: { scale: 1.14, x: "2.5%" } },
  orbit:       { initial: { scale: 1.1, rotate: -1.4 }, animate: { scale: 1.14, rotate: 1.4 } },
  rise:        { initial: { scale: 1.08, y: "3%" }, animate: { scale: 1.15, y: "-3%" } },
  crane:       { initial: { scale: 1.12, x: "-3%", y: "2%" }, animate: { scale: 1.12, x: "3%", y: "-2%" } },
  drift:       { initial: { scale: 1.1, x: "2%", y: "0%" }, animate: { scale: 1.12, x: "-2%", y: "-2%" } },
};

/* word-by-word kinetic type — 60-80ms stagger, 2% overshoot settle (directive) */
const Kinetic = ({ text, baseDelay = 0, className = "" }) => (
  <span className={className}>
    {text.split(" ").map((w, i) => (
      <motion.span
        key={`${w}-${i}`}
        className="inline-block mr-[0.3em]"
        initial={{ opacity: 0, y: 24, rotateX: -70, scale: 1.02 }}
        animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
        transition={{ delay: baseDelay + i * 0.07, duration: 0.5, ease: [0.2, 1.35, 0.4, 1] }}
      >
        {w}
      </motion.span>
    ))}
  </span>
);

/* minimal WebAudio score — 72 BPM sub pulse, metallic seal hits, the register chime */
const useScore = () => {
  const ctxRef = useRef(null);
  const pulseRef = useRef(null);
  const [on, setOn] = useState(false);

  const ctx = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return ctxRef.current;
  };
  const ping = (freq, dur, gain, type = "sine") => {
    const c = ctx();
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(gain, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
    o.connect(g).connect(c.destination);
    o.start();
    o.stop(c.currentTime + dur);
  };
  const seal = useCallback(() => {
    if (!ctxRef.current) return;
    ping(880, 0.18, 0.08, "square");
    ping(1760, 0.1, 0.04, "square");
  }, []);
  const chime = useCallback(() => {
    if (!ctxRef.current) return;
    ping(880, 1.1, 0.1);
    setTimeout(() => ping(1318.5, 1.4, 0.09), 140);
  }, []);
  const toggle = () => {
    if (on) {
      clearInterval(pulseRef.current);
      pulseRef.current = null;
      setOn(false);
      return;
    }
    ctx().resume?.();
    ping(55, 0.28, 0.22);
    pulseRef.current = setInterval(() => ping(55, 0.28, 0.22), 833); // 72 BPM heartbeat
    setOn(true);
  };
  useEffect(() => () => clearInterval(pulseRef.current), []);
  return { on, toggle, seal, chime };
};

export default function FilmPlayer() {
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const touchX = useRef(null);
  const score = useScore();
  const current = SCENES[scene];
  const move = MOVES[current.move];

  const go = useCallback((next, manual = true) => {
    const wrapped = (next + SCENES.length) % SCENES.length;
    if (SCENES[wrapped].chime) score.chime(); else score.seal();
    setScene(wrapped);
    if (manual) setPlaying(false); // viewer takes the wheel — auto-advance yields
  }, [score]);

  // auto-advance; a manual swipe/click pauses it (user paces the film)
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => go(scene + 1, false), current.duration);
    return () => clearTimeout(t);
  }, [playing, scene, current.duration, go]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(scene + 1);
      if (e.key === "ArrowLeft") go(scene - 1);
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scene, go]);

  const perCaption = current.duration / (current.captions.length + 0.5);

  return (
    <div
      data-testid="film-player"
      className="fixed inset-0 bg-black text-stone-100 overflow-hidden select-none"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 60) go(scene + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      {/* -------- animated frame -------- */}
      <AnimatePresence mode="popLayout">
        <motion.div
          key={scene}
          className="absolute inset-0"
          initial={current.whip ? { x: "70%", opacity: 0, filter: "blur(14px)" } : { opacity: 0, scale: 1.04 }}
          animate={{ x: "0%", opacity: 1, scale: 1, filter: "blur(0px)" }}
          exit={current.whip ? { x: "-70%", opacity: 0, filter: "blur(14px)" } : { opacity: 0, scale: 1.04 }}
          transition={current.whip ? { duration: 0.4, ease: "easeIn" } : { duration: 0.9, ease: "easeOut" }}
        >
          <motion.img
            data-testid="film-scene-image"
            src={current.img}
            alt={current.title}
            className="w-full h-full object-cover"
            initial={move.initial}
            animate={move.animate}
            transition={{ duration: current.duration / 1000, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      {/* cinematic vignette + letterbox */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.75)_100%)]" />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/85 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />

      {/* -------- top bar -------- */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 sm:px-8 py-4">
        <div data-testid="film-scene-tag" className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-amber-500/90">
          {current.tag}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="film-sound-btn"
            onClick={score.toggle}
            aria-label="toggle score"
            className="w-9 h-9 rounded-full border border-stone-700 bg-black/50 backdrop-blur flex items-center justify-center text-stone-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
          >
            {score.on ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          <a
            data-testid="film-exit-btn"
            href="/"
            aria-label="exit film"
            className="w-9 h-9 rounded-full border border-stone-700 bg-black/50 backdrop-blur flex items-center justify-center text-stone-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
          >
            <X size={15} />
          </a>
        </div>
      </div>

      {/* -------- captions -------- */}
      <div className="absolute inset-x-0 bottom-24 sm:bottom-28 px-6 text-center pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${scene}-title`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-stone-400 text-[10px] sm:text-xs uppercase tracking-[0.35em] mb-3"
          >
            {current.title}
          </motion.div>
        </AnimatePresence>
        <div data-testid="film-caption" className="min-h-[3.5rem] sm:min-h-[4.5rem] flex flex-col items-center justify-end gap-2 [text-shadow:0_2px_14px_rgba(0,0,0,0.95)]">
          {current.captions.map((phrase, j) => {
            const isLast = j === current.captions.length - 1;
            return (
              <div
                key={`${scene}-${j}`}
                className={
                  isLast
                    ? "text-2xl sm:text-4xl font-black tracking-tight text-stone-100"
                    : "font-mono text-[10px] sm:text-xs tracking-[0.3em]"
                }
                style={{
                  fontFamily: isLast ? "Georgia, serif" : undefined,
                  color: isLast ? undefined : j % 2 ? VIOLET : GOLD,
                }}
              >
                <Kinetic text={phrase} baseDelay={0.6 + (j * perCaption) / 1000} />
              </div>
            );
          })}
        </div>

        {/* end card doors */}
        {current.endcard && (
          <motion.div
            data-testid="film-endcard-ctas"
            className="mt-6 flex flex-wrap justify-center gap-3 pointer-events-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.2, duration: 0.7 }}
          >
            {[
              { href: "/wallet", label: "Member Wallet", icon: Smartphone, gold: true },
              { href: "/register", label: "Merchant Register", icon: Store },
              { href: "/portal", label: "Sovereign Portal", icon: Landmark },
            ].map(({ href, label, icon: Icon, gold }) => (
              <a
                key={href}
                data-testid={`film-cta-${label.toLowerCase().replace(/\s+/g, "-")}`}
                href={href}
                className={`inline-flex items-center gap-2 rounded-full font-bold text-xs uppercase tracking-wider px-5 py-2.5 transition-colors ${
                  gold
                    ? "bg-amber-500 text-black hover:bg-amber-400"
                    : "border border-amber-500/60 text-amber-500 bg-black/60 backdrop-blur-sm hover:bg-amber-500/20"
                }`}
              >
                <Icon size={13} /> {label}
              </a>
            ))}
            <a
              data-testid="film-cta-viewing-manual"
              href="/guide"
              className="inline-flex items-center gap-2 rounded-full border border-violet-400/60 text-violet-300 bg-black/60 backdrop-blur-sm font-bold text-xs uppercase tracking-wider px-5 py-2.5 hover:bg-violet-400/20 transition-colors"
            >
              Viewing Manual
            </a>
          </motion.div>
        )}
      </div>

      {/* -------- transport: progress + controls -------- */}
      <div className="absolute bottom-0 inset-x-0 px-5 sm:px-8 pb-5">
        <div className="flex items-center gap-1.5 mb-4">
          {SCENES.map((s, i) => (
            <button
              key={s.tag}
              data-testid={`film-progress-segment-${i + 1}`}
              onClick={() => go(i)}
              aria-label={`scene ${i + 1}`}
              className="group relative h-6 flex-1"
            >
              <span className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full ${i < scene ? "bg-amber-500" : "bg-stone-700 group-hover:bg-stone-500"}`} />
              {i === scene && (
                <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-stone-700 overflow-hidden">
                  <span
                    key={`fill-${scene}-${playing}`}
                    className="block h-full bg-amber-500"
                    style={{
                      animation: `film-seg ${current.duration}ms linear forwards`,
                      animationPlayState: playing ? "running" : "paused",
                    }}
                  />
                </span>
              )}
            </button>
          ))}
          <style>{`@keyframes film-seg { from { width: 0% } to { width: 100% } }`}</style>
        </div>
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] tracking-[0.25em] text-stone-500">
            {String(scene + 1).padStart(2, "0")} / {SCENES.length} · THE CASH VACUUM
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="film-prev-btn"
              onClick={() => go(scene - 1)}
              aria-label="previous scene"
              className="w-10 h-10 rounded-full border border-stone-700 bg-black/50 backdrop-blur flex items-center justify-center text-stone-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="film-play-btn"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? "pause" : "play"}
              className="w-12 h-12 rounded-full bg-amber-500 text-black flex items-center justify-center hover:bg-amber-400 transition-colors"
            >
              {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
            </button>
            <button
              data-testid="film-next-btn"
              onClick={() => go(scene + 1)}
              aria-label="next scene"
              className="w-10 h-10 rounded-full border border-stone-700 bg-black/50 backdrop-blur flex items-center justify-center text-stone-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
